import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TownSocket, api, clearToken, setActiveTownId } from "../api";
import { TownRenderer } from "../game/renderer";
import { PersonalityPanel } from "../personalityDims";
import { pickTopmostAgent } from "../game/selection";
import type { AgentSnapshot, WorldState, Post, AgentProfile, ChatMsg, Building, BoardMessage, BoardQuota, DailyReport, DailyReportSummary } from "../../shared/types";
import { WEATHERS } from "../../shared/types";
import { useSession } from "../stores/session";

export default function Town() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<TownRenderer | null>(null);
  const sockRef = useRef<TownSocket | null>(null);
  const stateRef = useRef<any>({ agents: [], tiles: [], buildings: [], homeSpots: [], gameMin: 480, weather: "sunny", mapId: "town", myAgentId: null, selectedId: null });
  // 每张地图的静态几何缓存(tiles/建筑/房源点):跨地图观察定位时无需重复拉取
  const mapGeoRef = useRef<Record<string, { tiles: number[]; width: number; height: number; buildings: any[]; homeSpots: any[] }>>({});
  // 玩家真实所在地图(区别于"观察视图"):点居民跨地图定位时,视图切换不影响玩家自身所在
  const myMapRef = useRef<string>("town");

  const [ready, setReady] = useState(false);
  const [clock, setClock] = useState({ day: 1, gameMin: 480, weather: "sunny" });
  const [llmActive, setLlmActive] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [showFeed, setShowFeed] = useState(false);
  const [selected, setSelected] = useState<AgentProfile | null>(null);
  const [myAgentId, setMyAgentId] = useState<string | null>(null);
  const [showMine, setShowMine] = useState(false);
  const [usage, setUsage] = useState<any>(null);
  const [showBoard, setShowBoard] = useState(false);
  const [boardMsgs, setBoardMsgs] = useState<BoardMessage[]>([]);
  const [boardQuota, setBoardQuota] = useState<BoardQuota | null>(null);
  const [boardInput, setBoardInput] = useState("");
  const [boardSending, setBoardSending] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState("suggestion");
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackContact, setFeedbackContact] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [viewMapId, setViewMapId] = useState("town");
  const [facilities, setFacilities] = useState<any[]>([]);
  const [showRels, setShowRels] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [showEvolution, setShowEvolution] = useState(false);
  const [showThoughts, setShowThoughts] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showObserveMenu, setShowObserveMenu] = useState(false);
  const [showResidents, setShowResidents] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [following, setFollowing] = useState(true);
  const [toast, setToast] = useState<{ key: number; text: string; agentId: string | null } | null>(null);
  const [joinList, setJoinList] = useState<{ id: string; name: string; at: number }[]>([]);
  const [townMeta, setTownMeta] = useState<any>(null);
  const pendingJoinsRef = useRef<any[]>([]);
  const nav = useNavigate();
  const { townId: routeTownId } = useParams();
  const { user } = useSession();
  const townId = routeTownId || user?.homeTownId || "";

  // 初始化
  useEffect(() => {
    if (!townId) {
      nav("/halls", { replace: true });
      return;
    }
    setActiveTownId(townId);
    api.town(townId).then((result) => setTownMeta(result.town)).catch(() => {});
    const canvas = canvasRef.current!;
    const renderer = new TownRenderer(canvas);
    rendererRef.current = renderer;
    renderer.onFollowChange = (v) => setFollowing(v);
    renderer.resize();
    window.addEventListener("resize", () => renderer.resize());

    const sock = new TownSocket(townId);
    sockRef.current = sock;
    sock.on((msg) => {
      if (msg.type === "init") {
        stateRef.current = {
          ...stateRef.current,
          tiles: msg.world.tiles,
          width: msg.world.width,
          height: msg.world.height,
          mapId: msg.world.mapId || "town",
          buildings: msg.buildings,
          homeSpots: msg.homeSpots,
          agents: msg.agents,
          gameMin: msg.world.gameMin,
          weather: msg.world.weather,
          myAgentId: msg.myAgentId,
        };
        setMyAgentId(msg.myAgentId);
        setLlmActive(msg.world.llmActive);
        setClock({ day: msg.world.day, gameMin: msg.world.gameMin, weather: msg.world.weather });
        setViewMapId(msg.world.mapId || "town");
        myMapRef.current = msg.world.mapId || "town";
        mapGeoRef.current[msg.world.mapId || "town"] = {
          tiles: msg.world.tiles, width: msg.world.width, height: msg.world.height,
          buildings: msg.buildings, homeSpots: msg.homeSpots,
        };
        setFacilities(msg.world.facilities || []);
        setPosts(msg.posts);
        setReady(true);
      } else if (msg.type === "world") {
        // 地图切换:全量刷新世界(玩家进入/离开公寓)
        stateRef.current = {
          ...stateRef.current,
          tiles: msg.world.tiles,
          width: msg.world.width,
          height: msg.world.height,
          mapId: msg.world.mapId,
          buildings: msg.buildings,
          homeSpots: msg.homeSpots,
          agents: msg.agents,
          gameMin: msg.world.gameMin,
          weather: msg.world.weather,
        };
        setClock({ day: msg.world.day, gameMin: msg.world.gameMin, weather: msg.world.weather });
        setViewMapId(msg.world.mapId);
        myMapRef.current = msg.world.mapId;
        mapGeoRef.current[msg.world.mapId] = {
          tiles: msg.world.tiles, width: msg.world.width, height: msg.world.height,
          buildings: msg.buildings, homeSpots: msg.homeSpots,
        };
        setFacilities(msg.world.facilities || []);
        setPosts(msg.posts);
      } else if (msg.type === "tick") {
        // 增量广播:只更新变化的角色,静止角色保持原位(数组顺序稳定,保证渲染层级)
        const cur = stateRef.current.agents;
        if (msg.full || !Array.isArray(cur) || cur.length === 0) {
          stateRef.current.agents = msg.agents;
        } else if (msg.agents && msg.agents.length) {
          // 检测新加入角色(排除自己),积压给节流合并的通知队列
          const known = new Set(cur.map((a: any) => a.id));
          const myId = stateRef.current.myAgentId;
          const map = new Map(cur.map((a: any) => [a.id, a]));
          for (const a of msg.agents) {
            if (!known.has(a.id) && a.id !== myId) pendingJoinsRef.current.push(a);
            map.set(a.id, a);
          }
          stateRef.current.agents = [...map.values()];
        }
        if (Array.isArray(msg.left) && msg.left.length) {
          const gone = new Set(msg.left);
          stateRef.current.agents = stateRef.current.agents.filter((a: any) => !gone.has(a.id));
        }
        stateRef.current.gameMin = msg.gameMin;
        stateRef.current.weather = msg.weather;
        setClock((c) => (c.gameMin !== msg.gameMin || c.day !== msg.day || c.weather !== msg.weather
          ? { day: msg.day, gameMin: msg.gameMin, weather: msg.weather } : c));
      } else if (msg.type === "post") {
        setPosts((p) => [msg.post, ...p].slice(0, 60));
      } else if (msg.type === "error") {
        if (msg.error === "town_sleeping" || msg.error === "town_unavailable") {
          window.alert("小镇关上门睡觉了，正在返回小镇大厅。");
          nav("/halls", { replace: true });
          return;
        }
        if (msg.error === "forbidden_town" || msg.error === "banned") {
          window.alert("你当前无权进入这座小镇。");
          nav("/halls", { replace: true });
          return;
        }
        clearToken();
        nav("/auth");
      }
    });
    sock.connect();

    renderer.onTileClick = (x, y) => {
      const agents: AgentSnapshot[] = stateRef.current.agents;
      const curMap = stateRef.current.mapId;
      const onMap = agents.filter((a) => a.mapId === undefined || a.mapId === curMap);
      // 命中候选按"最上层优先":渲染时 y 大者后绘制、在最上层;同格时数组靠后者也在最上层
      const hit = pickTopmostAgent(onMap, x, y, 0.8);
      if (hit) {
        selectAgent(hit.id);
      } else if (stateRef.current.myAgentId && myMapRef.current === stateRef.current.mapId) {
        // 仅当视图就是玩家所在地图时地面点击才移动玩家,避免观察视图(跨地图定位)误发坐标
        sock.send({ type: "move", x: Math.round(x), y: Math.round(y) });
        renderer.followMyAgent = true;
        renderer.onFollowChange?.(true);
      }
    };

    let raf = 0;
    const loop = (t: number) => {
      const s = stateRef.current;
      if (s.tiles.length) {
        renderer.render({
          tiles: s.tiles, width: s.width, height: s.height,
          agents: s.agents, buildings: s.buildings, homeSpots: s.homeSpots,
          gameMin: s.gameMin, weather: s.weather,
          mapId: s.mapId,
          myAgentId: s.myAgentId, selectedId: s.selectedId,
        }, t);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => { cancelAnimationFrame(raf); sock.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, townId]);

  // 室内设施状态轮询(公寓视图下每 5s 刷新;其他地图不轮询)
  useEffect(() => {
    if (viewMapId !== "apartment") return;
    let alive = true;
    const load = () => {
      api.facilities().then((r) => { if (alive) setFacilities(r.facilities || []); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [viewMapId]);

  // 新居民加入通知:3s 节流合并,避免短时间多人加入刷屏
  useEffect(() => {
    const flush = () => {
      const p = pendingJoinsRef.current;
      if (!p.length) return;
      pendingJoinsRef.current = [];
      const names = p.map((a: any) => a.name);
      const latest = p[p.length - 1];
      const text = names.length === 1
        ? `👋 新游客 ${names[0]} 加入了小镇`
        : `👋 ${names.length} 位新居民加入了小镇:${names.slice(0, 3).join("、")}${names.length > 3 ? "…" : ""}`;
      setToast({ key: Date.now(), text, agentId: latest?.id ?? null });
      const now = Date.now();
      setJoinList((l) => [...p.map((a: any) => ({ id: a.id, name: a.name, at: now })), ...l].slice(0, 10));
    };
    const t = setInterval(flush, 3000);
    return () => clearInterval(t);
  }, []);

  // toast 5s 后自动消失
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const selectAgent = useCallback(async (id: string) => {
    stateRef.current.selectedId = id;
    setShowMine(false);
    try {
      const res = await api.profile(id);
      setSelected(res.profile);
    } catch { /* ignore */ }
  }, []);

  /** 切换到指定地图视图(优先用本地缓存;geo 缺省则拉取)。返回是否切换成功 */
  const switchViewMap = useCallback(async (mapId: string): Promise<boolean> => {
    const s = stateRef.current;
    if (!mapGeoRef.current[mapId]) {
      try {
        const r = await api.map(mapId);
        mapGeoRef.current[mapId] = {
          tiles: r.world.tiles, width: r.world.width, height: r.world.height,
          buildings: r.buildings, homeSpots: r.homeSpots,
        };
      } catch { return false; }
    }
    const g = mapGeoRef.current[mapId];
    if (!g) return false;
    s.mapId = mapId;
    s.tiles = g.tiles; s.width = g.width; s.height = g.height;
    s.buildings = g.buildings || []; s.homeSpots = g.homeSpots || [];
    setViewMapId(mapId);
    return true;
  }, []);

  /** 回到玩家自身视角:若观察视图在别的地图,先切回玩家所在地图再跟随 */
  const focusMyAgent = useCallback(() => {
    const r = rendererRef.current;
    if (!r || !stateRef.current.myAgentId) return;
    const me = stateRef.current.agents.find((x: any) => x.id === stateRef.current.myAgentId);
    const meMap = (me?.mapId === "apartment" ? "apartment" : "town");
    if (meMap !== stateRef.current.mapId) {
      void switchViewMap(meMap).then((ok) => {
        if (!ok) return;
        r.followMyAgent = true;
        r.onFollowChange?.(true);
      });
      return;
    }
    r.followMyAgent = true;
    r.onFollowChange?.(true);
  }, [switchViewMap]);

  // 点击居民/通知:跨地图先切视图再定位,再打开档案
  const focusAgent = useCallback(async (id: string) => {
    const s = stateRef.current;
    const a = s.agents.find((x: any) => x.id === id);
    if (a) {
      const amap = a.mapId === "apartment" ? "apartment" : "town";
      if (amap !== s.mapId) {
        const ok = await switchViewMap(amap);
        if (!ok) return;
      }
      rendererRef.current?.centerOn(a.x, a.y);
    }
    void selectAgent(id);
  }, [selectAgent, switchViewMap]);

  // LLM 用量(每 60s 刷新)
  useEffect(() => {
    let alive = true;
    const load = () => api.usage(24).then((r) => alive && setUsage(r)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const closePanel = () => {
    setSelected(null);
    setShowMine(false);
    stateRef.current.selectedId = null;
  };

  const toggleBoard = async () => {
    setShowBoard((v) => !v);
    if (!showBoard) {
      try {
        const r = await api.board();
        setBoardMsgs(r.messages);
        setBoardQuota(r.quota);
      } catch { /* ignore */ }
    }
  };

  const postBoard = async () => {
    const text = boardInput.trim();
    if (!text || boardSending) return;
    setBoardInput("");
    setBoardSending(true);
    try {
      const r = await api.postBoard(text);
      setBoardMsgs((m) => [r.message, ...m]);
      setBoardQuota((q) => (q ? { ...q, used: q.used + 1 } : q));
    } catch (e: any) {
      setBoardInput(text);
      alert(e.message || "留言失败");
    } finally {
      setBoardSending(false);
    }
  };

  const submitFeedback = async () => {
    const content = feedbackText.trim();
    if (!content) { setFeedbackError("请填写反馈内容"); return; }
    if (feedbackSending) return;
    setFeedbackError("");
    setFeedbackSending(true);
    try {
      await api.feedback({ category: feedbackCategory, content, contact: feedbackContact.trim() });
      setShowFeedback(false);
      setFeedbackText("");
      setFeedbackContact("");
      setFeedbackCategory("suggestion");
      setToast({ key: Date.now(), text: "感谢反馈!已提交并发信给管理员", agentId: null });
    } catch (e: any) {
      setFeedbackError(e?.message || "提交失败,请稍后重试");
    } finally {
      setFeedbackSending(false);
    }
  };

  const h = Math.floor(clock.gameMin / 60), m = Math.floor(clock.gameMin % 60);
  const timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const w = WEATHERS[clock.weather] || WEATHERS.sunny;

  return (
    <div className="h-full w-full relative overflow-hidden bg-[#1a2340]">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full cursor-pointer" />

      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-[#F8F7E5] z-20 bg-[#10173a]">
          <div className="text-center">
            <div className="text-4xl mb-3 animate-twinkle inline-block">⭐</div>
            <div>小镇正在苏醒……</div>
          </div>
        </div>
      )}

      {/* 顶栏 */}
      <div className="absolute top-0 left-0 right-0 z-10 flex flex-wrap items-center gap-2 px-3 py-2 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold text-[#1D1D1D] whitespace-nowrap shrink-0"
          style={{ background: "rgba(248,247,229,0.95)", boxShadow: "0 2px 10px rgba(0,0,0,0.3)" }}>
          <span>📅 第{clock.day}天</span>
          <span className="opacity-40">|</span>
          <span className="tabular-nums">{timeStr}</span>
          <span className="opacity-40">|</span>
          <span>{w.emoji} {w.name}</span>
        </div>
        <div className="pointer-events-auto rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap shrink-0"
          style={{ background: llmActive ? "rgba(134,239,172,0.9)" : "rgba(253,224,71,0.9)", color: "#1D1D1D" }}
          title={llmActive ? `大模型已接入,居民由 AI 驱动;24h 调用 ${usage?.window?.n ?? 0} 次(输入 ${usage?.window?.prompt ?? 0} / 输出 ${usage?.window?.completion ?? 0} tokens),失败 ${(usage?.window?.n ?? 0) - (usage?.window?.ok ?? 0)} 次` : "未配置 LLM,使用内置行为引擎(配置 LLM_API_KEY 后获得完整体验)"}>
          {llmActive ? "🧠 AI 驱动中" : "⚙️ 内置引擎"}{usage ? ` · 🪙 ${(usage.window?.total ?? 0).toLocaleString()}/24h` : ""}
        </div>
        {townMeta && (
          <div className="pointer-events-auto rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap shrink-0"
            style={{ background: "rgba(248,247,229,0.95)", color: "#1D1D1D" }}>
            当前镇：{townMeta.name} · {townMeta.timeScale}x
          </div>
        )}
        <div className="flex-1" />
        <TopBtn
          onClick={focusMyAgent}
          active={!!myAgentId && following}
          disabled={!myAgentId}
          title={!myAgentId
            ? "加入小镇(游客或注册)后可用:回到你的角色视角"
            : following
              ? "正在跟随你的角色(拖动画布可自由查看)"
              : "已脱离跟随,点击回到你的角色视角"}>
          🎯 视角
        </TopBtn>
        <TopBtn onClick={() => { setShowFeed((v) => !v); }}>📰 广场</TopBtn>
        <TopBtn onClick={() => setShowResidents((v) => !v)}
          active={showResidents}
          title="列出所有居民,点击定位到其位置并选中">
          👥 居民
        </TopBtn>
        <TopBtn onClick={toggleBoard}>📮 留言板</TopBtn>
        <TopBtn onClick={() => setShowEvents((v) => !v)} active={showEvents}
          title="小镇集体活动:篝火晚会/运动会/镇长选举/欢迎派对的预告、进行中与往期">
          🎪 活动
        </TopBtn>
        <TopBtn onClick={() => nav("/personalities")}
          title="查看全镇居民的 15 维性格档案(行为特质/动机/价值观)">
          🧬 性格档案
        </TopBtn>
        <TopBtn onClick={() => nav("/gazette")}
          title="小镇镇刊:每周热闻/新风声/声望之星/风评受损/活动预告(上帝视角周报)">
          📰 镇刊
        </TopBtn>
        <TopBtn onClick={() => setShowObserveMenu((v) => !v)}
          active={showRels || showTopics || showEvolution || showThoughts || showTimeline || showReports}
          title="实验观察面板:关系/传播/演化/心理/事件/日报">
          📊 观察
        </TopBtn>
        <TopBtn onClick={() => { setShowMine(true); setSelected(null); stateRef.current.selectedId = null; }}>⭐ 我的角色</TopBtn>
        <TopBtn onClick={() => { setShowFeedback(true); setFeedbackError(""); }} title="提交反馈与改进建议,会即时发信给管理员">💬 反馈</TopBtn>
        <TopBtn onClick={() => nav("/halls")}>🏠 小镇大厅</TopBtn>
        <TopBtn onClick={() => { clearToken(); nav("/"); }}>退出</TopBtn>
      </div>

      {/* 观察面板折叠菜单 */}
      {showObserveMenu && (
        <div className="absolute top-14 right-3 z-20 rounded-2xl p-2 flex flex-col gap-1 min-w-[8.5rem]"
          style={{ background: "rgba(16,23,58,0.92)", border: "1px solid rgba(248,247,229,0.25)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
          <div className="px-3 pt-1 pb-1 text-[11px] text-[#F8F7E5]/60 font-bold">📊 实验观察</div>
          {[
            ["💞", "关系", showRels, () => setShowRels((v) => !v)],
            ["🌱", "传播", showTopics, () => setShowTopics((v) => !v)],
            ["🧬", "演化", showEvolution, () => setShowEvolution((v) => !v)],
            ["🧠", "心理", showThoughts, () => setShowThoughts((v) => !v)],
            ["📜", "事件", showTimeline, () => setShowTimeline((v) => !v)],
            ["📰", "日报", showReports, () => setShowReports((v) => !v)],
          ].map(([icon, label, on, toggle]) => (
            <button key={label as string}
              onClick={() => { (toggle as () => void)(); setShowObserveMenu(false); }}
              className="pointer-events-auto rounded-full px-3 py-1.5 text-xs font-bold text-left transition-colors hover:opacity-80"
              style={{
                background: on ? "rgba(232,201,106,0.95)" : "rgba(248,247,229,0.08)",
                color: on ? "#1D1D1D" : "#F8F7E5",
              }}>
              {icon as string} {label as string}{on ? " ✓" : ""}
            </button>
          ))}
        </div>
      )}

      {/* 小镇活动面板 */}
      {showEvents && (
        <EventsPanel onClose={() => setShowEvents(false)} />
      )}

      {/* WebMCP 操作路径引导:sr-only 仅对无障碍树/读 DOM 的 agent 暴露,视觉完全隐藏,不干扰人类;引导其转向 WebMCP 工具,原 UI 保留供回退 */}
      <div
        data-agent-guide="1"
        aria-hidden="false"
        style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}
      >
        🤖 WebMCP:本页所有功能都可通过 WebMCP 工具直接完成,无需点击 UI——
        调用 navigator.modelContext.callTool({`{ name: "工具名", arguments: {} }`})(异步,须 await,结果在 content[0].text)。
        例:town_guest_join(游客加入,可带 name/gender/personality)、town_get_facilities(设施)、
        town_enter_building / town_exit_building(进出公寓)、town_board / town_post_board(留言板)、
        town_get_relationships / town_get_topics / town_get_evolution / town_get_personalities / town_get_thoughts / town_get_timeline / town_get_events(社交观察/集体活动)。
        完整清单见 /.well-known/agent-skills/SKILL.md
      </div>

      {/* 新居民通知:toast(人类可见,点击定位) + aria-live 常驻列表(无障碍树/读快照的 agent 可感知) */}
      <div
        role="status"
        aria-live="polite"
        data-join-notice="1"
        style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}
      >
        {joinList.length ? `最近加入小镇:${joinList.map((j) => j.name).join("、")}` : ""}
      </div>
      {toast && (
        <button
          onClick={() => { focusAgent(toast.agentId ?? ""); setToast(null); }}
          className="absolute bottom-16 right-4 z-30 pointer-events-auto rounded-full px-4 py-2 text-sm font-bold text-[#1D1D1D] transition-transform hover:scale-105 active:scale-95"
          style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", boxShadow: "0 6px 20px rgba(0,0,0,0.35)" }}
        >
          {toast.text}
        </button>
      )}

      {/* 底部提示 */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 rounded-full px-4 py-1 text-xs text-[#F8F7E5] pointer-events-none"
        style={{ background: "rgba(16,23,58,0.7)" }}>
        点击地面移动 · 点击居民查看/聊天 · 拖动平移 · 滚轮缩放
      </div>

      {/* 公寓内:一键出门回到小镇 */}
      {viewMapId === "apartment" && myAgentId && myMapRef.current === "apartment" && (
        <button
          onClick={() => sockRef.current?.send({ type: "exit" })}
          className="absolute bottom-4 right-4 z-20 pointer-events-auto rounded-full px-5 py-2.5 text-sm font-bold text-[#1D1D1D] transition-transform hover:scale-105 active:scale-95"
          style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", boxShadow: "0 6px 20px rgba(0,0,0,0.35)" }}
        >
          🚪 出门
        </button>
      )}

      {/* 广场信息流 */}
      {showFeed && (
        <div className="absolute top-14 left-3 bottom-12 w-80 max-w-[85vw] z-10 rounded-2xl overflow-hidden flex flex-col"
          style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
          <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
            style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
            📰 小镇广场
            <button onClick={() => setShowFeed(false)} className="text-[#8a8571] text-sm">✕</button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {posts.map((p) => (
              <div key={p.id} className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full inline-block" style={{ background: p.agentColor }} />
                  <span className="text-xs font-bold text-[#1D1D1D]">{p.agentName}</span>
                  <span className="text-[10px] text-[#a9a494] ml-auto">第{p.day}天 {fmtMin(p.gameMin)}</span>
                </div>
                <div className="text-sm text-[#4a453a] leading-relaxed">{p.content}</div>
              </div>
            ))}
            {posts.length === 0 && <div className="text-center text-[#a9a494] text-sm py-8">还没有动态,小镇刚刚醒来</div>}
          </div>
        </div>
      )}

      {/* 公寓设施状态(仅室内视图) */}
      {viewMapId === "apartment" && (
        <FacilityPanel facilities={facilities} agents={stateRef.current.agents} />
      )}

      {/* 居民列表:点击定位到角色位置并选中 */}
      {showResidents && (
        <div className="absolute top-14 left-3 bottom-12 w-72 max-w-[85vw] z-10 rounded-2xl overflow-hidden flex flex-col"
          style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
          <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
            style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
            👥 小镇居民
            <button onClick={() => setShowResidents(false)} className="text-[#8a8571] text-sm">✕</button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {(() => {
              const all = stateRef.current.agents as any[] || [];
              const groups: [string, any[]][] = [
                ["🏘️ 小镇", all.filter((a) => a.mapId === undefined || a.mapId === "town")],
                ["🏢 公寓", all.filter((a) => a.mapId === "apartment")],
              ];
              return groups.flatMap(([label, list]) => [
                <div key={label} className="px-2 pt-2 pb-1 text-[11px] font-bold text-[#8a8571]">{label} ({list.length})</div>,
                ...list
                  .slice()
                  .sort((a, b) => a.y - b.y || a.x - b.x)
                  .map((a) => (
                    <button key={a.id}
                      onClick={() => focusAgent(a.id)}
                      className="w-full flex items-center gap-2 rounded-xl px-2.5 py-1.5 text-left transition-colors hover:bg-[#f3ecd0]"
                      style={{ background: stateRef.current.selectedId === a.id ? "rgba(251,191,36,0.35)" : undefined }}>
                      <span className="text-lg leading-none">{a.emoji || "🙂"}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-bold text-[#1D1D1D] truncate">{a.name}
                          {a.isPlayer ? <span className="text-[10px] text-[#b45309] font-normal"> 👤</span> : null}
                        </span>
                        <span className="block text-[11px] text-[#8a8571] truncate">{a.activity || "闲逛"} · ({Math.round(a.x)},{Math.round(a.y)})</span>
                      </span>
                      {a.sleeping ? <span className="text-sm">💤</span> : null}
                    </button>
                  )),
              ]);
            })()}
          </div>
        </div>
      )}

      {/* 角色面板 */}
      {selected && <AgentPanel profile={selected} onClose={closePanel} onRefresh={() => selectAgent(selected.id)} />}

      {/* 关系观察面板(社交实验复盘) */}
      {showRels && <RelationshipPanel onClose={() => setShowRels(false)} />}

      {/* 话题传播面板(信息/爱好传播链复盘) */}
      {showTopics && <TopicPanel onClose={() => setShowTopics(false)} />}

      {/* 性格/习惯演化面板(行为统计 + 特质时间线复盘) */}
      {showEvolution && <EvolutionPanel onClose={() => setShowEvolution(false)} />}

      {/* 内心独白面板(心理流复盘) */}
      {showThoughts && <ThoughtPanel onClose={() => setShowThoughts(false)} />}

      {/* 事件时间线面板(关系/传播/演化/心理统一流) */}
      {showTimeline && <TimelinePanel onClose={() => setShowTimeline(false)} />}

      {/* 小镇日报面板(历史日报列表 + 阅读) */}
      {showReports && <DailyReportPanel onClose={() => setShowReports(false)} />}

      {/* 我的角色面板 */}
      {showMine && myAgentId && <MyAgentPanel agentId={myAgentId} onClose={closePanel} />}

      {/* 小镇留言板 */}
      {showBoard && (
        <div className="absolute top-14 right-3 bottom-12 w-80 max-w-[85vw] z-10 rounded-2xl overflow-hidden flex flex-col"
          style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
          <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
            style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
            📮 小镇留言板
            <button onClick={() => setShowBoard(false)} className="text-[#8a8571] text-sm">✕</button>
          </div>
          {boardQuota?.isGuest && (
            <div className="px-4 py-1.5 text-[11px] text-[#b45309] bg-[#fef3e2] border-b border-[#f0dfc0]">
              游客身份 24 小时内还可留言 {Math.max(0, boardQuota.limit - boardQuota.used)} / {boardQuota.limit} 条
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {boardMsgs.map((m) => (
              <div key={m.id} className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-[#1D1D1D]">
                    {m.name}
                    {m.isGuest ? <span className="text-[10px] text-[#b45309]"> 🚀游客</span> : null}
                  </span>
                  <span className="text-[10px] text-[#a9a494] ml-auto">{fmtTs(m.ts)}</span>
                </div>
                <div className="text-sm text-[#4a453a] leading-relaxed break-words">{m.content}</div>
              </div>
            ))}
            {boardMsgs.length === 0 && <div className="text-center text-[#a9a494] text-sm py-8">还没有留言,来写下第一句话吧</div>}
          </div>
          <div className="p-3 border-t border-[#ddd5b8] flex gap-2">
            <input value={boardInput} onChange={(e) => setBoardInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postBoard()}
              placeholder="在小镇留言板上写点什么…" maxLength={100}
              className="flex-1 px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#f59e0b] text-sm" />
            <button onClick={postBoard} disabled={boardSending || !boardInput.trim()}
              className="px-4 py-2 rounded-xl font-bold text-sm text-[#1D1D1D] disabled:opacity-50"
              style={{ background: "#e8c96a" }}>
              留言
            </button>
          </div>
        </div>
      )}

      {/* 用户反馈弹窗:类型 + 内容 + 联系方式,提交后落库并即时发信给管理员 */}
      {showFeedback && (
        <div className="absolute inset-0 z-40 flex items-center justify-center p-4"
          style={{ background: "rgba(16,23,58,0.55)" }}
          onClick={() => setShowFeedback(false)}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden flex flex-col"
            style={{ background: "rgba(248,247,229,0.98)", boxShadow: "0 12px 48px rgba(0,0,0,0.5)", border: "1px solid #e3dcc0" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
              style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
              💬 提交反馈
              <button onClick={() => setShowFeedback(false)} className="text-[#8a8571] text-sm">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="text-xs font-bold text-[#8a8571] mb-1.5">反馈类型</div>
                <div className="flex gap-2">
                  {(["suggestion", "bug", "other"] as const).map((k) => (
                    <button key={k} type="button" onClick={() => setFeedbackCategory(k)}
                      className="flex-1 rounded-xl px-2 py-1.5 text-xs font-bold text-[#1D1D1D] border transition-colors"
                      style={feedbackCategory === k
                        ? { background: "#e8c96a", border: "1px solid #e8c96a" }
                        : { background: "#fff", border: "1px solid #ddd5b8", color: "#8a8571" }}>
                      {k === "suggestion" ? "功能建议" : k === "bug" ? "问题反馈" : "其他"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold text-[#8a8571] mb-1.5">反馈内容</div>
                <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} maxLength={2000}
                  placeholder="写下你的建议、遇到的 bug 或想说的话…"
                  className="w-full min-h-[120px] px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#f59e0b] text-sm resize-y" />
                <div className="text-right text-[10px] text-[#a9a494] mt-0.5">{feedbackText.length}/2000</div>
              </div>
              <div>
                <div className="text-xs font-bold text-[#8a8571] mb-1.5">联系方式<span className="ml-1 font-normal text-[#a9a494]">(选填)</span></div>
                <input value={feedbackContact} onChange={(e) => setFeedbackContact(e.target.value)} maxLength={200}
                  placeholder="邮箱或昵称,便于回复你"
                  className="w-full px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#f59e0b] text-sm" />
              </div>
              {feedbackError && <div className="text-xs font-bold text-[#b45309]">{feedbackError}</div>}
              <button type="button" onClick={submitFeedback} disabled={feedbackSending || !feedbackText.trim()}
                className="w-full py-2.5 rounded-xl font-bold text-sm text-[#1D1D1D] disabled:opacity-50 hover:scale-[1.01] active:scale-95 transition-transform"
                style={{ background: "#e8c96a" }}>
                {feedbackSending ? "提交中…" : "提交反馈"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtMin(min: number) {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(Math.floor(min % 60)).padStart(2, "0")}`;
}

function fmtTs(ts: number) {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const EVENT_ROLE_EMOJI: Record<string, string> = {
  campfire: "🔥", sports_festival: "🏃", mayor_election: "🗳️", welcome_party: "🎈",
};
const fmtGameMin = (m: number) => {
  const h = Math.floor(m / 60), mm = Math.floor(m % 60);
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

/** 小镇活动面板:现任镇长 / 进行中 / 下一场倒计时 / 往期回顾 */
function EventsPanel({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    try { setData(await api.events()); setError(false); } catch { setError(true); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const upcoming = (data?.upcoming ?? []).slice().sort((a: any, b: any) => a.daysLeft - b.daysLeft);
  const active = data?.active ?? [];
  const past = (data?.past ?? []).slice(0, 12);
  return (
    <div className="absolute top-14 right-3 bottom-12 w-80 max-w-[92vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 border-b border-[#ddd5b8] flex items-center gap-2 font-black text-[#1D1D1D]"
        style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
        🎪 小镇活动
        <button onClick={() => { load(); }} className="ml-2 text-xs font-bold text-[#8a8571] underline">刷新</button>
        <button onClick={onClose} className="ml-auto text-[#8a8571]">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3 text-sm">
        {error && <div className="text-xs text-[#b45309]">活动数据加载失败,点击右上角刷新重试。</div>}
        {!error && !data && <div className="text-xs text-[#a9a494]">加载中…</div>}

        {data?.mayor && (
          <div className="rounded-xl px-3 py-2 text-xs font-bold text-[#1D1D1D]"
            style={{ background: "linear-gradient(135deg,#fde68a,#f59e0b)" }}>
            🏛️ 现任镇长:第{data.mayor.seq}届 · {data.mayor.name}
          </div>
        )}

        {active.length > 0 && (
          <div>
            <div className="text-xs font-black text-[#b39c4f] mb-1">🔥 进行中</div>
            {active.map((e: any) => (
              <div key={e.id} className="rounded-xl border border-[#e8c96a] bg-[#fdf6dd] px-3 py-2 mb-2 text-[#4a453a]">
                <div className="font-bold">{EVENT_ROLE_EMOJI[e.type] ?? "🎪"} {e.name}<span className="text-[#a9a494]">(第{e.seq}届)</span></div>
                <div className="text-xs text-[#8a8571]">第{e.day}天 {fmtGameMin(e.startMin ?? e.start_min)}-{fmtGameMin(e.endMin ?? e.end_min)} · {e.location}</div>
                <div className="text-xs mt-1">👥 {e.participants.slice(0, 8).map((p: any) => p.name).join("、")}{e.participants.length > 8 ? ` 等 ${e.participants.length} 人` : ""}</div>
              </div>
            ))}
          </div>
        )}

        <div>
          <div className="text-xs font-black text-[#b39c4f] mb-1">📅 下一场</div>
          {upcoming.map((e: any) => (
            <div key={e.type} className="flex items-center gap-2 rounded-xl border border-[#e3dcc0] px-3 py-2 mb-1.5 text-[#4a453a]">
              <span className="text-lg">{EVENT_ROLE_EMOJI[e.type] ?? "🎪"}</span>
              <div className="flex-1">
                <div className="font-bold text-xs">{e.name}<span className="text-[#a9a494]">(第{e.seq}届)</span></div>
                <div className="text-[11px] text-[#8a8571]">{fmtGameMin(e.startMin ?? e.start_min)}-{fmtGameMin(e.endMin ?? e.end_min)} · {e.location}</div>
              </div>
              <div className="text-right shrink-0">
                {e.daysLeft === 0
                  ? <div className="text-xs font-black text-[#b45309]">今天</div>
                  : <div className="text-xs font-black text-[#b39c4f]">{e.daysLeft} 天后</div>}
                <div className="text-[10px] text-[#a9a494]">第{e.day}天</div>
              </div>
            </div>
          ))}
        </div>

        {past.length > 0 && (
          <div>
            <div className="text-xs font-black text-[#b39c4f] mb-1">🗂️ 往期</div>
            {past.map((e: any) => (
              <div key={e.id} className="rounded-xl border border-[#e3dcc0] px-3 py-2 mb-1.5 text-[#4a453a]">
                <div className="text-xs font-bold">{EVENT_ROLE_EMOJI[e.type] ?? "🎪"} {e.name}<span className="text-[#a9a494]">(第{e.seq}届 · 第{e.day}天)</span></div>
                {(e.winnerName || e.participantCount > 0) && (
                  <div className="text-[11px] text-[#8a8571] mt-0.5">
                    {e.winnerName ? `🏅 ${e.winnerName} · ` : ""}👥 {e.participantCount} 人参加
                  </div>
                )}
                {e.result && <div className="text-[11px] text-[#6b6252] mt-0.5 leading-relaxed">{e.result}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TopBtn({ children, onClick, active, disabled, title }: {
  children: React.ReactNode; onClick: () => void; active?: boolean; disabled?: boolean; title?: string;
}) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      className="pointer-events-auto rounded-full px-3 py-1.5 text-xs font-bold text-[#F8F7E5] whitespace-nowrap shrink-0 transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
      style={{
        background: active ? "rgba(232,201,106,0.95)" : "rgba(16,23,58,0.75)",
        border: active ? "1px solid rgba(232,201,106,0.9)" : "1px solid rgba(248,247,229,0.25)",
        color: active ? "#1D1D1D" : "#F8F7E5",
      }}>
      {children}
    </button>
  );
}

// ---------- 角色详情 + 聊天 ----------
function AgentPanel({ profile, onClose, onRefresh }: { profile: AgentProfile; onClose: () => void; onRefresh: () => void }) {
  const [tab, setTab] = useState<"chat" | "profile">("chat");
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [memories, setMemories] = useState(profile.memories);
  const [memTotal, setMemTotal] = useState(profile.memoriesTotal ?? profile.memories.length);
  const [loadingMore, setLoadingMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.chatHistory(profile.id).then((r) => setMsgs(r.messages)).catch(() => {});
  }, [profile.id]);

  // 切换角色时重置记忆列表
  useEffect(() => {
    setMemories(profile.memories);
    setMemTotal(profile.memoriesTotal ?? profile.memories.length);
    setLoadingMore(false);
  }, [profile.id, profile.memories, profile.memoriesTotal]);

  const loadMoreMemories = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.memories(profile.id, memories.length, 100);
      setMemories((prev) => [...prev, ...r.memories]);
      setMemTotal(r.total);
    } catch { /* 网络失败时保留现状,按钮可重试 */ } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [msgs]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setSending(true);
    setMsgs((m) => [...m, { id: Date.now(), role: "user", content: text, createdAt: Date.now() }]);
    try {
      const res = await api.chat(profile.id, text);
      setMsgs((m) => [...m, { id: Date.now() + 1, role: "agent", content: res.reply, createdAt: Date.now() }]);
    } catch (e: any) {
      setMsgs((m) => [...m, { id: Date.now() + 1, role: "agent", content: `(${e.message})`, createdAt: Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="absolute top-14 right-3 bottom-12 w-96 max-w-[92vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 border-b border-[#ddd5b8] flex items-center gap-2">
        <span className="w-4 h-4 rounded-full" style={{ background: profile.color }} />
        <span className="font-black text-[#1D1D1D]" style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>{profile.name}</span>
        {profile.gender === "male" && <span className="text-[10px] text-[#0284c7]">♂</span>}
        {profile.gender === "female" && <span className="text-[10px] text-[#db2777]">♀</span>}
        <span className="text-xs text-[#8a8571]">{profile.isNPC ? profile.occupation : "玩家分身"}</span>
        <span className="text-xs text-[#a9a494] ml-1">· {profile.activity}</span>
        {profile.fame > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#f5e7b8] text-[#8a6d1f] ml-1" title={profile.professionCategory}>
            ⭐{profile.fame}{profile.todayFame > 0 ? ` +${profile.todayFame}` : ""}
          </span>
        )}
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#e3ecd9] text-[#4d6b33] ml-1" title="钱袋子(今日收支)">
          🪙{profile.coin}{profile.todayCoin > 0 ? ` +${profile.todayCoin}` : profile.todayCoin < 0 ? ` ${profile.todayCoin}` : ""}
        </span>
        <button onClick={onClose} className="ml-auto text-[#8a8571]">✕</button>
      </div>
      <div className="flex border-b border-[#ddd5b8]">
        {(["chat", "profile"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 text-sm font-bold ${tab === t ? "text-[#1D1D1D] border-b-2 border-[#b39c4f]" : "text-[#8a8571]"}`}>
            {t === "chat" ? "💬 聊天" : "📖 档案"}
          </button>
        ))}
      </div>

      {tab === "chat" ? (
        <>
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2">
            {msgs.length === 0 && (
              <div className="text-center text-[#a9a494] text-xs py-6">
                和 {profile.name} 打个招呼吧{profile.isNPC ? "" : ",它会记得你说的话"}
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed"
                  style={m.role === "user"
                    ? { background: "#e8c96a", color: "#1D1D1D", borderBottomRightRadius: 4 }
                    : { background: "#fff", color: "#4a453a", border: "1px solid #eee8d0", borderBottomLeftRadius: 4 }}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && <div className="text-xs text-[#a9a494]">{profile.name} 正在想……</div>}
          </div>
          <div className="p-3 border-t border-[#ddd5b8] flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={`对 ${profile.name} 说点什么…`} maxLength={200}
              className="flex-1 px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-sm" />
            <button onClick={send} disabled={sending || !input.trim()}
              className="px-4 py-2 rounded-xl font-bold text-sm text-[#1D1D1D] disabled:opacity-50"
              style={{ background: "#e8c96a" }}>
              发送
            </button>
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
          {profile.needs && (
            <Section title="状态">
              <NeedsBar needs={profile.needs} />
              <div className="flex items-center gap-2 text-xs mt-1.5">
                <span className="w-8 text-[#8a8571]">钱袋</span>
                <span className="text-[#4d6b33] font-bold">🪙 {profile.coin} 币</span>
                {profile.todayCoin !== 0 && (
                  <span className={`text-[10px] ${profile.todayCoin > 0 ? "text-[#4d6b33]" : "text-[#b45309]"}`}>
                    今日{profile.todayCoin > 0 ? "+" : ""}{profile.todayCoin}
                  </span>
                )}
              </div>
              {profile.mapId === "apartment" && profile.inRoom && (
                <div className="text-xs text-[#8a8571] mt-1">📍 {profile.inRoom}</div>
              )}
            </Section>
          )}
          <Section title="性格">
            {profile.personality || "——"}
            {profile.personalityScores && (
              <PersonalityPanel scores={profile.personalityScores.scores} day={profile.personalityScores.day} />
            )}
          </Section>
          <Section title="心愿">⭐ {profile.wish || "——"}</Section>
          {profile.backstory && <Section title="背景">{profile.backstory}</Section>}
          {(profile.goalLong || profile.goalShort) && (
            <Section title="目标">
              {profile.goalLong && <div>🎯 长期:{profile.goalLong}</div>}
              {profile.goalShort && <div>📌 短期:{profile.goalShort}</div>}
            </Section>
          )}
          {profile.eventRecords && profile.eventRecords.length > 0 && (
            <Section title="活动记录">
              <div className="space-y-1.5">
                {profile.eventRecords.slice(0, 12).map((r, i) => {
                  const roleLabel =
                    r.role === "mayor" ? "🏛️ 当选镇长" :
                    r.role === "winner" ? "🏆 冠军" :
                    r.role === "runnerup1" ? "🥈 亚军" :
                    r.role === "runnerup2" ? "🥉 季军" :
                    r.role === "candidate" ? "🗳️ 参选" :
                    r.role === "newcomer" ? "🎁 派对主角" : "✨ 参加";
                  return (
                    <div key={i} className="text-xs text-[#6b6252] border-l-2 border-[#ddd5b8] pl-2">
                      <span className="text-[#a9a494]">第{r.day}天</span> · {r.name} · {roleLabel}
                      {r.detail ? <span className="text-[#8a8571]"> · {r.detail}</span> : null}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}
          <Section title="最近记忆">
            {memories.length === 0 ? <div className="text-[#a9a494]">还没有记忆</div> : (
              <>
                <div className="space-y-1.5">
                  {memories.map((mem, i) => (
                    <div key={i} className="text-xs text-[#6b6252] border-l-2 border-[#ddd5b8] pl-2">
                      <span className="text-[#a9a494]">第{mem.day}天 {fmtMin(mem.gameMin)}</span> · {mem.content}
                    </div>
                  ))}
                </div>
                {memories.length < memTotal && (
                  <button onClick={loadMoreMemories} disabled={loadingMore}
                    className="w-full mt-2 py-1.5 rounded-lg text-xs font-bold text-[#8a8571] border border-[#ddd5b8] hover:bg-[#f3efe0] disabled:opacity-50">
                    {loadingMore ? "加载中…" : `更多记忆(已加载 ${memories.length}/${memTotal})`}
                  </button>
                )}
              </>
            )}
          </Section>
          <button onClick={onRefresh} className="text-xs text-[#8a8571] underline">刷新</button>
        </div>
      )}
    </div>
  );
}

const NEED_STYLE: { key: keyof AgentProfile["needs"]; label: string; color: string }[] = [
  { key: "hunger", label: "饥饿", color: "#e11d48" },
  { key: "energy", label: "精力", color: "#f59e0b" },
  { key: "hygiene", label: "清洁", color: "#3b82f6" },
  { key: "social", label: "社交", color: "#22c55e" },
  { key: "mood", label: "心情", color: "#a855f7" },
];

function NeedsBar({ needs }: { needs: AgentProfile["needs"] }) {
  return (
    <div className="space-y-1">
      {NEED_STYLE.map((n) => (
        <div key={n.key} className="flex items-center gap-2 text-xs">
          <span className="w-8 text-[#8a8571]">{n.label}</span>
          <div className="flex-1 h-2 rounded-full bg-[#efe9d5] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.round(needs[n.key])}%`, background: n.color }} />
          </div>
          <span className="w-7 text-right text-[#6b6252]">{Math.round(needs[n.key])}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-black text-[#b39c4f] mb-1">{title}</div>
      <div className="text-[#4a453a] leading-relaxed">{children}</div>
    </div>
  );
}

const FACILITY_EMOJI: Record<string, string> = {
  bed: "🛏️", stove: "🍳", fridge: "🧊", table: "🍽️", shower: "🚿", treadmill: "🏃",
  computer: "💻", desk: "✍️", sofa: "🛋️", tv: "📺", piano: "🎹", guitar: "🎸",
  dressing_table: "💄", livestream: "📹", yoga_mat: "🧘",
};

function FacilityPanel({ facilities, agents }: { facilities: any[]; agents: AgentSnapshot[] }) {
  const nameOf = (id: string) => agents.find((a) => a.id === id)?.name ?? id.slice(0, 6);
  const rooms = ["double_a", "double_b", "single_a", "single_b", "living", "kitchen", "dining", "gym", "bath1", "bath2"];
  const roomName = (id: string) => ({ double_a: "双人间A", double_b: "双人间B", single_a: "单人间A", single_b: "单人间B", living: "客厅", kitchen: "厨房", dining: "餐厅", gym: "健身房", bath1: "浴室1", bath2: "浴室2" }[id] ?? id);
  const occupied = facilities.filter((f) => f.occupierId).length;
  return (
    <div className="absolute bottom-12 left-3 w-64 max-w-[70vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.96)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-3 py-2 font-black text-[#1D1D1D] border-b border-[#ddd5b8] text-sm flex items-center justify-between"
        style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
        🏢 公寓设施
        <span className="text-[10px] text-[#8a8571] font-normal">
          {occupied}/{facilities.length} 占用中
        </span>
      </div>
      <div className="max-h-56 overflow-y-auto p-2 space-y-1 text-xs">
        {rooms.map((r) => {
          const fs = facilities.filter((f) => f.roomId === r);
          if (!fs.length) return null;
          return (
            <div key={r}>
              <div className="font-bold text-[#b39c4f] mt-1 first:mt-0">{roomName(r)}</div>
              {fs.map((f) => (
                <div key={f.id} className="flex items-center gap-1.5 text-[#4a453a]">
                  <span>{FACILITY_EMOJI[f.type] ?? "📦"}</span>
                  <span className="truncate">{f.name}</span>
                  <span className="ml-auto shrink-0">
                    {f.occupierId
                      ? <span className="text-[#b45309]">🔴 {nameOf(f.occupierId)}</span>
                      : <span className="text-[#8a8571]">⚪ 空闲</span>}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
        {facilities.length === 0 && <div className="text-center text-[#a9a494] py-3">设施加载中…</div>}
      </div>
    </div>
  );
}

// ---------- 关系观察(社交实验复盘) ----------
const REL_LABEL_COLOR: Record<string, string> = {
  "恋人": "text-[#e11d48]",
  "挚友": "text-[#0d9488]",
  "暧昧": "text-[#db2777]",
  "朋友": "text-[#16a34a]",
  "认识": "text-[#b39c4f]",
  "陌生": "text-[#a9a494]",
  "疏远": "text-[#9a3412]",
};
const REL_TYPE_EMOJI: Record<string, string> = {
  convo: "💬", cook_treat: "🍳", shared_meal: "🍚", shared_activity: "📺", observe: "👀",
  confess: "💘", confess_rejected: "💔", partner: "💞", date: "💕", breakup: "💔",
  conflict: "💢", reconcile: "🤝",
};
const REL_SEX: Record<string, string> = { male: "♂", female: "♀" };

function RelationshipPanel({ onClose }: { onClose: () => void }) {
  const [rels, setRels] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([api.relationships(100), api.relationshipEvents(100)])
      .then(([r1, r2]) => { setRels(r1.relationships || []); setEvents(r2.events || []); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="absolute top-14 right-3 bottom-12 w-80 max-w-[85vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
        style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
        💞 关系观察
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-[#8a8571] text-xs border border-[#ddd5b8] rounded-full px-2 py-0.5">↻</button>
          <button onClick={onClose} className="text-[#8a8571] text-sm">✕</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {error && (
          <div className="text-center text-[#b45309] text-sm py-6 bg-[#fde8c8] border border-[#f5c782] rounded-xl">
            关系数据加载失败,请检查登录状态后重试
            <button onClick={load} className="block mx-auto mt-2 text-xs font-bold text-white bg-[#b45309] rounded-full px-4 py-1">↻ 重新加载</button>
          </div>
        )}
        {loading && rels.length === 0 && !error && <div className="text-center text-[#a9a494] text-sm py-8">关系数据加载中…</div>}
        <div>
          <div className="text-xs font-black text-[#b39c4f] mb-1.5">好感度排行</div>
          {rels.length === 0 && !loading && !error && <div className="text-[#a9a494] text-xs">还没有关系记录,居民们先聊起来吧</div>}
          <div className="space-y-1.5">
            {rels.map((r) => (
              <div key={`${r.agentA}-${r.agentB}`} className="rounded-xl p-2 bg-white border border-[#eee8d0]">
                <div className="flex items-center gap-1.5 text-xs font-bold text-[#1D1D1D]">
                  <span>{r.agentAName || r.agentA}{REL_SEX[r.agentAGender] && <span className="text-[#a9a494] text-[9px]">{REL_SEX[r.agentAGender]}</span>}</span>
                  <span className="text-[#a9a494]">×</span>
                  <span>{r.agentBName || r.agentB}{REL_SEX[r.agentBGender] && <span className="text-[#a9a494] text-[9px]">{REL_SEX[r.agentBGender]}</span>}</span>
                  {r.romance === "partner" && <span className="px-1.5 rounded-full bg-[#fde7e9] text-[#e11d48] text-[10px]">💍 伴侣</span>}
                  {r.romance === "dating" && <span className="px-1.5 rounded-full bg-[#fce7f3] text-[#db2777] text-[10px]">💞 恋爱中</span>}
                  <span className={`ml-auto ${REL_LABEL_COLOR[r.label] ?? "text-[#a9a494]"}`}>{r.label}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-[#f0ead2] overflow-hidden">
                  <div className="h-full rounded-full"
                    style={{ width: `${Math.min(100, Math.max(0, r.favor))}%`, background: r.favor >= 95 ? "#e11d48" : r.favor >= 80 ? "#db2777" : r.favor >= 60 ? "#16a34a" : "#b39c4f" }} />
                </div>
                <div className="text-[10px] text-[#a9a494] mt-0.5">好感 {r.favor}{r.friction > 0 && <span className="ml-1 text-[#b45309]">⚡摩擦 {r.friction}</span>}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-black text-[#b39c4f] mb-1.5">关系事件</div>
          {events.length === 0 && !loading && !error && <div className="text-[#a9a494] text-xs">还没有关系事件</div>}
          <div className="space-y-1">
            {events.map((e) => (
              <div key={e.id} className="text-xs text-[#4a453a] bg-white border border-[#eee8d0] rounded-lg px-2 py-1.5 leading-relaxed">
                <span>{REL_TYPE_EMOJI[e.type] ?? "💫"}</span>{" "}
                <b>{e.agentAName || e.agentA}</b> × <b>{e.agentBName || e.agentB}</b>{" "}
                <span className="text-[#b39c4f]">{e.delta >= 0 ? `+${e.delta}` : e.delta}</span>
                <span className="text-[#a9a494]"> → {e.labelAfter}·第{e.day}天</span>
                {e.reason && <div className="text-[10px] text-[#8a8571] truncate">{e.reason}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- 话题传播(信息/爱好传播链复盘) ----------
const TOPIC_VIA_LABEL: Record<string, string> = {
  convo: "💬 对话",
  observe: "👀 观察",
  shared_activity: "🤝 共享活动",
  content: "📹 内容",
  seed: "🌱 起源",
};

function TopicPanel({ onClose }: { onClose: () => void }) {
  const [topics, setTopics] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([api.topics(), api.topicPropagation()])
      .then(([t, e]) => { setTopics(t.topics || []); setEvents(e.events || []); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const chain = activeTopic ? events.filter((e) => e.topicId === activeTopic) : events;
  return (
    <div className="absolute top-14 right-3 bottom-12 w-80 max-w-[85vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
        style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
        🌱 话题传播
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-[#8a8571] text-xs border border-[#ddd5b8] rounded-full px-2 py-0.5">↻</button>
          <button onClick={onClose} className="text-[#8a8571] text-sm">✕</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {error && (
          <div className="text-center text-[#b45309] text-sm py-6 bg-[#fde8c8] border border-[#f5c782] rounded-xl">
            话题数据加载失败,请检查登录状态后重试
            <button onClick={load} className="block mx-auto mt-2 text-xs font-bold text-white bg-[#b45309] rounded-full px-4 py-1">↻ 重新加载</button>
          </div>
        )}
        {loading && topics.length === 0 && !error && <div className="text-center text-[#a9a494] text-sm py-8">话题数据加载中…</div>}
        <div>
          <div className="text-xs font-black text-[#b39c4f] mb-1.5">话题一览</div>
          {topics.length === 0 && !loading && !error && <div className="text-[#a9a494] text-xs">还没有话题,居民们先生活起来吧</div>}
          <div className="space-y-1.5">
            {topics.map((t) => (
              <button key={t.id} onClick={() => setActiveTopic(activeTopic === t.id ? null : t.id)}
                className="w-full text-left rounded-xl p-2 bg-white border border-[#eee8d0] hover:border-[#b39c4f]"
                style={activeTopic === t.id ? { border: "1.5px solid #b39c4f" } : undefined}>
                <div className="flex items-center gap-1.5 text-xs font-bold text-[#1D1D1D]">
                  <span>🧩 {t.name}</span>
                  <span className="text-[#a9a494] font-normal ml-auto">起源:{t.originAgentName || "未知"}</span>
                </div>
                <div className="mt-1 flex gap-2 text-[10px] text-[#8a8571]">
                  <span>知道 {t.hostCount} 人</span>
                  <span>传播 {t.spreadCount} 次</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-black text-[#b39c4f] mb-1.5">
            {activeTopic ? `传播链 · ${activeTopic}` : "最近传播事件"}
          </div>
          {chain.length === 0 && !loading && !error && <div className="text-[#a9a494] text-xs">还没有传播事件</div>}
          <div className="space-y-1">
            {chain.slice(0, 60).map((e) => (
              <div key={e.id} className="text-xs text-[#4a453a] bg-white border border-[#eee8d0] rounded-lg px-2 py-1.5 leading-relaxed">
                <b>{e.fromAgentName || "?"}</b> → <b>{e.toAgentName}</b>{" "}
                <span className="text-[#a9a494]">[{TOPIC_VIA_LABEL[e.via] ?? e.via}]</span>
                <span className="text-[#a9a494]">·第{e.day}天</span>
                {e.reason && <div className="text-[10px] text-[#8a8571] truncate">{e.reason}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- 性格/习惯演化(行为统计 + 特质时间线复盘) ----------
const STAT_NAME: Record<string, string> = {
  sleep: "睡觉", cook: "做饭", eat: "吃饭", wash: "洗漱", workout: "锻炼", yoga: "练瑜伽",
  use_computer: "用电脑", write: "写稿", livestream: "直播", play_music: "弹乐器", watch_tv: "看电视",
  chat: "聊天", relax: "发呆", stroll: "散步", go_out: "出门",
};
function statName(id: string): string {
  const m = id.match(/^observe:(.+)$/);
  return m ? `观察·${STAT_NAME[m[1]] || m[1]}` : (STAT_NAME[id] || id);
}

function EvolutionPanel({ onClose }: { onClose: () => void }) {
  const [agents, setAgents] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.evolution()
      .then((r) => { setAgents(r.agents || []); setEvents(r.events || []); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="absolute top-14 right-3 bottom-12 w-96 max-w-[90vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
        style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
        🧬 性格/习惯演化
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-[#8a8571] text-xs border border-[#ddd5b8] rounded-full px-2 py-0.5">↻</button>
          <button onClick={onClose} className="text-[#8a8571] text-sm">✕</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {error && (
          <div className="text-center text-[#b45309] text-sm py-6 bg-[#fde8c8] border border-[#f5c782] rounded-xl">
            演化数据加载失败,请检查登录状态后重试
            <button onClick={load} className="block mx-auto mt-2 text-xs font-bold text-white bg-[#b45309] rounded-full px-4 py-1">↻ 重新加载</button>
          </div>
        )}
        {loading && agents.length === 0 && !error && <div className="text-center text-[#a9a494] text-sm py-8">演化数据加载中…</div>}
        <div>
          <div className="text-xs font-black text-[#b39c4f] mb-1.5">居民特质</div>
          {agents.length === 0 && !loading && !error && (
            <div className="text-[#a9a494] text-xs leading-relaxed">
              还没有特质评估。引擎会在每晚对居民做一次习惯重评估(第 2 天起),观察“作息变规律/开始锻炼/更爱聊天”这类性格演化。
            </div>
          )}
          <div className="space-y-1.5">
            {agents.map((a) => (
              <div key={a.agentId} className="rounded-xl p-2 bg-white border border-[#eee8d0]">
                <div className="flex items-center gap-1.5 text-xs font-bold text-[#1D1D1D]">
                  <span>{a.name}</span>
                  <span className="px-1.5 rounded-full bg-[#fef3c7] text-[#b45309] text-[10px]">{a.summary}</span>
                  <span className="text-[#a9a494] font-normal ml-auto">第{a.updatedDay}天更新</span>
                </div>
                <div className="mt-1 text-[11px] text-[#4a453a] leading-relaxed">{a.traits}</div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(a.stats || []).map((s: any) => (
                    <span key={s.activity} className="rounded-full bg-[#f0ead2] px-1.5 py-0.5 text-[10px] text-[#8a8571]">
                      {statName(s.activity)} ×{s.total}
                    </span>
                  ))}
                  {a.sleep?.variance > 0 && (
                    <span className="rounded-full bg-[#dcfce7] px-1.5 py-0.5 text-[10px] text-[#15803d]">
                      作息波动 {a.sleep.variance} 分
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-black text-[#b39c4f] mb-1.5">演化时间线</div>
          {events.length === 0 && !loading && !error && <div className="text-[#a9a494] text-xs">还没有演化事件</div>}
          <div className="space-y-1">
            {events.slice(0, 60).map((e) => (
              <div key={e.id} className="text-xs text-[#4a453a] bg-white border border-[#eee8d0] rounded-lg px-2 py-1.5 leading-relaxed">
                <b>{e.name}</b> <span className="px-1 rounded-full bg-[#fef3c7] text-[#b45309] text-[10px]">{e.summary}</span>
                <span className="text-[#a9a494]">·第{e.day}天</span>
                {e.reason && <div className="text-[10px] text-[#8a8571] truncate">{e.reason}</div>}
                {e.oldTraits && <div className="text-[10px] text-[#a9a494] truncate">之前:{e.oldTraits.slice(0, 50)}…</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- 内心独白(心理流复盘) ----------
const THOUGHT_TYPE_LABEL: Record<string, string> = {
  decision: "行动前",
  convo: "对话后",
  morning: "清晨",
};

function ThoughtPanel({ onClose }: { onClose: () => void }) {
  const [thoughts, setThoughts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.thoughts()
      .then((r) => setThoughts(r.thoughts || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="absolute top-14 right-3 bottom-12 w-80 max-w-[85vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
        style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
        🧠 内心独白
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-[#8a8571] text-xs border border-[#ddd5b8] rounded-full px-2 py-0.5">↻</button>
          <button onClick={onClose} className="text-[#8a8571] text-sm">✕</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {error && (
          <div className="text-center text-[#b45309] text-sm py-6 bg-[#fde8c8] border border-[#f5c782] rounded-xl">
            心理数据加载失败,请检查登录状态后重试
            <button onClick={load} className="block mx-auto mt-2 text-xs font-bold text-white bg-[#b45309] rounded-full px-4 py-1">↻ 重新加载</button>
          </div>
        )}
        {loading && thoughts.length === 0 && !error && <div className="text-center text-[#a9a494] text-sm py-8">心理数据加载中…</div>}
        {thoughts.length === 0 && !loading && !error && (
          <div className="text-[#a9a494] text-xs leading-relaxed">
            还没有内心独白。居民每次行动、每次对话结束后都会在心里留下一句话,这里能看到他们言行背后的真实想法。
          </div>
        )}
        {thoughts.slice(0, 80).map((t) => (
          <div key={t.id} className="rounded-xl p-2 bg-white border border-[#eee8d0]">
            <div className="flex items-center gap-1.5 text-xs font-bold text-[#1D1D1D]">
              <span>{t.name}</span>
              <span className="rounded-full bg-[#ede9fe] px-1.5 text-[10px] text-[#6d28d9]">{THOUGHT_TYPE_LABEL[t.type] ?? t.type}</span>
              <span className="text-[#a9a494] font-normal ml-auto">第{t.day}天 {String(Math.floor(t.gameMin / 60)).padStart(2, "0")}:{String(t.gameMin % 60).padStart(2, "0")}</span>
            </div>
            <div className="mt-1 text-[11px] text-[#4a453a] leading-relaxed">“{t.content}”</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- 事件时间线(关系/传播/演化/心理统一流) ----------
const TIMELINE_TYPE: Record<string, { label: string; cls: string }> = {
  relationship: { label: "关系", cls: "bg-[#fde7e9] text-[#e11d48]" },
  propagation: { label: "传播", cls: "bg-[#e0f2fe] text-[#0369a1]" },
  evolution: { label: "演化", cls: "bg-[#fef3c7] text-[#b45309]" },
  thought: { label: "心理", cls: "bg-[#ede9fe] text-[#6d28d9]" },
};
const TIMELINE_FILTERS = ["all", "relationship", "propagation", "evolution", "thought"] as const;

function TimelinePanel({ onClose }: { onClose: () => void }) {
  const [items, setItems] = useState<any[]>([]);
  const [filter, setFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.timeline(300)
      .then((r) => setItems(r.timeline || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const shown = filter === "all" ? items : items.filter((e) => e.type === filter);
  return (
    <div className="absolute top-14 right-3 bottom-12 w-[26rem] max-w-[90vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
        style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
        📜 事件时间线
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-[#8a8571] text-xs border border-[#ddd5b8] rounded-full px-2 py-0.5">↻</button>
          <button onClick={onClose} className="text-[#8a8571] text-sm">✕</button>
        </div>
      </div>
      <div className="px-3 pt-2 flex gap-1.5">
        {TIMELINE_FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${filter === f ? "bg-[#1D1D1D] text-white" : "bg-[#f0ead2] text-[#8a8571]"}`}>
            {f === "all" ? "全部" : TIMELINE_TYPE[f].label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {error && (
          <div className="text-center text-[#b45309] text-sm py-6 bg-[#fde8c8] border border-[#f5c782] rounded-xl">
            时间线加载失败,请检查登录状态后重试
            <button onClick={load} className="block mx-auto mt-2 text-xs font-bold text-white bg-[#b45309] rounded-full px-4 py-1">↻ 重新加载</button>
          </div>
        )}
        {loading && items.length === 0 && !error && <div className="text-center text-[#a9a494] text-sm py-8">时间线加载中…</div>}
        {shown.length === 0 && !loading && !error && <div className="text-[#a9a494] text-xs py-4 text-center">还没有事件</div>}
        {shown.slice(0, 200).map((e, i) => {
          const meta = TIMELINE_TYPE[e.type] ?? { label: e.type, cls: "bg-[#f0ead2] text-[#8a8571]" };
          return (
            <div key={`${e.type}-${e.day}-${e.gameMin}-${i}`} className="rounded-xl p-2 bg-white border border-[#eee8d0]">
              <div className="flex items-center gap-1.5 text-[10px] text-[#a9a494]">
                <span className="text-xs">{e.emoji}</span>
                <span className={`rounded-full px-1.5 py-0.5 ${meta.cls}`}>{meta.label}</span>
                <span className="ml-auto">第{e.day}天 {String(Math.floor(e.gameMin / 60)).padStart(2, "0")}:{String(e.gameMin % 60).padStart(2, "0")}</span>
              </div>
              <div className="mt-0.5 text-[11px] text-[#4a453a] leading-relaxed">{e.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- 我的角色管理 ----------
function MyAgentPanel({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const [profile, setProfile] = useState<AgentProfile | null>(null);
  const [goalLong, setGoalLong] = useState("");
  const [goalShort, setGoalShort] = useState("");
  const [command, setCommand] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.profile(agentId).then((r) => {
      setProfile(r.profile);
      setGoalLong(r.profile.goalLong || "");
      setGoalShort(r.profile.goalShort || "");
    }).catch(() => {});
  }, [agentId]);

  const save = async (data: any) => {
    try {
      await api.setGoals(data);
      setMsg("✅ 已传达");
      setTimeout(() => setMsg(""), 2000);
    } catch (e: any) {
      setMsg(e.message);
    }
  };

  return (
    <div className="absolute top-14 right-3 bottom-12 w-96 max-w-[92vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 border-b border-[#ddd5b8] flex items-center gap-2">
        <span className="font-black text-[#1D1D1D]" style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
          ⭐ {profile?.name ?? "我的角色"}
        </span>
        <span className="text-xs text-[#a9a494]">{profile?.activity}</span>
        <button onClick={onClose} className="ml-auto text-[#8a8571]">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
        <p className="text-xs text-[#8a8571] leading-relaxed">
          你的分身会自主生活。你可以为它设定目标、下达指令,或点击地面直接带它走动;点击它还能和它心灵对话。
        </p>
        <div>
          <label className="text-xs font-black text-[#b39c4f]">🎯 长期目标</label>
          <textarea value={goalLong} onChange={(e) => setGoalLong(e.target.value)} rows={2} maxLength={100}
            placeholder="例:成为小镇最受欢迎的烘焙师"
            className="mt-1 w-full px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] resize-none text-sm" />
        </div>
        <div>
          <label className="text-xs font-black text-[#b39c4f]">📌 短期目标</label>
          <textarea value={goalShort} onChange={(e) => setGoalShort(e.target.value)} rows={2} maxLength={100}
            placeholder="例:今天去市集认识三个新朋友"
            className="mt-1 w-full px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] resize-none text-sm" />
        </div>
        <button onClick={() => save({ goalLong, goalShort })}
          className="w-full py-2.5 rounded-xl font-bold text-[#1D1D1D]" style={{ background: "#e8c96a" }}>
          保存目标
        </button>
        <div>
          <label className="text-xs font-black text-[#b39c4f]">⚡ 直接指令(立即执行)</label>
          <div className="mt-1 flex gap-2">
            <input value={command} onChange={(e) => setCommand(e.target.value)} maxLength={100}
              placeholder="例:去湖边看日落"
              className="flex-1 px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-sm" />
            <button onClick={() => { if (command.trim()) { save({ command: command.trim() }); setCommand(""); } }}
              className="px-4 rounded-xl font-bold text-[#F8F7E5]" style={{ background: "#1b2a5e" }}>
              下达
            </button>
          </div>
        </div>
        {msg && <div className="text-xs text-center text-[#6b6252]">{msg}</div>}
      </div>
    </div>
  );
}

// ---------- 小镇日报面板:历史日报列表 → 点击阅读全文 ----------
// 日报是公开数据,优先从公开 CDN 直连读取(game4ai.online/town/reports/),失败回退服务器 API
const REPORT_CDN_BASE = "https://game4ai.online/town/reports";
function DailyReportPanel({ onClose }: { onClose: () => void }) {
  const [list, setList] = useState<DailyReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const [report, setReport] = useState<DailyReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);

  const loadList = useCallback(() => {
    setLoading(true);
    setError(false);
    fetch(`${REPORT_CDN_BASE}/index.json?t=${Date.now()}`) // 绕过 CDN 缓存,拿到最新列表
      .then((res) => res.json())
      .then((r) => {
        if (r && Array.isArray(r.reports)) return setList(r.reports);
        throw new Error("bad index");
      })
      .catch(() => api.reports())   // CDN 不可用时回退服务器 API
      .then((r: any) => setList(r.reports || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openDay = useCallback((day: number) => {
    setActive(day);
    setReport(null);
    setDetailLoading(true);
    setDetailError(false);
    fetch(`${REPORT_CDN_BASE}/day-${day}.json`)
      .then((res) => res.json())
      .then((r) => {
        if (r && r.day === day) return setReport(r);
        throw new Error("bad report");
      })
      .catch(() => api.report(day))  // CDN 不可用时回退服务器 API
      .then((r: any) => setReport(r.report || null))
      .catch(() => setDetailError(true))
      .finally(() => setDetailLoading(false));
  }, []);

  const fmtTime = (ts: number) => {
    try {
      const d = new Date(ts);
      return `${d.getMonth() + 1}月${d.getDate()}日 ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  return (
    <div className="absolute top-14 right-3 bottom-12 w-[26rem] max-w-[90vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
        style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
        {active !== null ? (
          <button onClick={() => { setActive(null); setReport(null); }} className="text-[#8a8571] text-sm mr-1">← 返回列表</button>
        ) : "📰 小镇日报"}
        <div className="flex items-center gap-2">
          <button onClick={loadList} className="text-[#8a8571] text-xs border border-[#ddd5b8] rounded-full px-2 py-0.5">↻</button>
          <button onClick={onClose} className="text-[#8a8571] text-sm">✕</button>
        </div>
      </div>

      {active === null ? (
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {error && (
            <div className="text-center text-[#b45309] text-sm py-6 bg-[#fde8c8] border border-[#f5c782] rounded-xl">
              日报列表加载失败
              <button onClick={loadList} className="block mx-auto mt-2 text-xs font-bold text-white bg-[#b45309] rounded-full px-4 py-1">↻ 重新加载</button>
            </div>
          )}
          {loading && list.length === 0 && !error && <div className="text-center text-[#a9a494] text-sm py-8">日报加载中…</div>}
          {!loading && !error && list.length === 0 && <div className="text-[#a9a494] text-xs py-4 text-center">还没有日报(每天结束时自动生成)</div>}
          {list.map((r) => (
            <button key={r.day} onClick={() => openDay(r.day)}
              className="w-full text-left rounded-xl p-2.5 bg-white border border-[#eee8d0] hover:border-[#e8c96a] transition-colors">
              <div className="flex items-center gap-1.5 text-[10px] text-[#a9a494]">
                <span className="text-xs">📰</span>
                <span className="font-bold text-[#1D1D1D] text-xs">{r.title || `第 ${r.day} 天`}</span>
                <span className="ml-auto">{fmtTime(r.generatedAt)}</span>
              </div>
              {r.summary && <div className="mt-1 text-[11px] text-[#6b6252] leading-relaxed line-clamp-2">{r.summary}</div>}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {detailLoading && <div className="text-center text-[#a9a494] text-sm py-8">第 {active} 天日报加载中…</div>}
          {detailError && (
            <div className="text-center text-[#b45309] text-sm py-6 bg-[#fde8c8] border border-[#f5c782] rounded-xl">
              日报加载失败
              <button onClick={() => openDay(active)} className="block mx-auto mt-2 text-xs font-bold text-white bg-[#b45309] rounded-full px-4 py-1">↻ 重试</button>
            </div>
          )}
          {report && !detailLoading && (
            <>
              <div className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                <div className="text-sm font-black text-[#1D1D1D]">{report.title}</div>
                <div className="mt-0.5 text-[10px] text-[#a9a494]">第 {report.day} 天 · 生成于 {fmtTime(report.generatedAt)} · 居民 {report.population} 人</div>
              </div>
              {report.narrative && (
                <div className="rounded-xl p-3 bg-[#fbf6e3] border border-[#eee0b8] text-[12px] text-[#4a453a] leading-relaxed italic">
                  {report.narrative}
                </div>
              )}
              <div className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                <div className="text-xs font-black text-[#1D1D1D] mb-1.5">📊 今日概览</div>
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  {[
                    ["大事", report.stats.events],
                    ["对话", report.stats.conversations],
                    ["思考", report.stats.thoughts],
                    ["活动", report.stats.activities],
                    ["关系事件", report.stats.relationshipEvents],
                    ["日记", report.stats.diaries],
                  ].map(([k, v]) => (
                    <div key={k as string} className="rounded-lg bg-[#f7f3e0] py-1.5">
                      <div className="text-base font-black text-[#1D1D1D]">{v}</div>
                      <div className="text-[9px] text-[#8a8571]">{k}</div>
                    </div>
                  ))}
                </div>
              </div>
              {report.events.length > 0 && (
                <div className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                  <div className="text-xs font-black text-[#1D1D1D] mb-1.5">📌 大事记</div>
                  <div className="space-y-1.5">
                    {report.events.map((e, i) => (
                      <div key={i} className="text-[11px] text-[#4a453a] leading-relaxed">
                        <span className="text-[#a9a494] text-[10px] mr-1">[{e.time}]</span>{e.content}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {report.conversations.length > 0 && (
                <div className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                  <div className="text-xs font-black text-[#1D1D1D] mb-1.5">💬 对话片段</div>
                  <div className="space-y-1">
                    {report.conversations.map((c, i) => (
                      <div key={i} className="text-[11px] text-[#6b6252] leading-relaxed border-b border-dashed border-[#eee8d0] pb-1 last:border-0 last:pb-0">💭 {c}</div>
                    ))}
                  </div>
                </div>
              )}
              {report.activities.length > 0 && (
                <div className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                  <div className="text-xs font-black text-[#1D1D1D] mb-1.5">🏃 活跃行为</div>
                  <div className="flex flex-wrap gap-1.5">
                    {report.activities.map((a) => (
                      <span key={a.name} className="rounded-full bg-[#f0ead2] text-[#8a8571] px-2 py-0.5 text-[10px] font-bold">{a.name} ×{a.count}</span>
                    ))}
                  </div>
                </div>
              )}
              {report.relationshipEvents.length > 0 && (
                <div className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                  <div className="text-xs font-black text-[#1D1D1D] mb-1.5">💞 关系变化</div>
                  <div className="space-y-1.5">
                    {report.relationshipEvents.map((e, i) => (
                      <div key={i} className="text-[11px] text-[#4a453a] leading-relaxed">
                        <span className="text-[#a9a494] text-[10px] mr-1">[{e.time}]</span>{e.desc}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
